import { useCallback, useRef } from 'react';
import type { StoreApi, UseBoundStore } from 'zustand';
import type { ConversationState } from '../state/conversationStore';
import type { ChatRequest, ModelId, SSEEvent } from '../providers/types';
import { streamGeminiChat } from '../providers/gemini';
import { streamOpenAIChat } from '../providers/openai';
import { CONVERSATION_PROMPT } from '../prompt';

export interface UseStreamingChatOptions {
  store: UseBoundStore<StoreApi<ConversationState>>;
}

export interface UseStreamingChat {
  sendSegment: (audioB64: string, durationSec: number, model: ModelId) => Promise<void>;
}

// Rationale: an in-render `busy: boolean` on the returned shape would be stale
// (reads busyRef.current at render time, never re-runs on flip). The store's
// `status === 'inferring'` is already the canonical UI signal; UI components
// read that directly instead of a derived field on this hook.

export function useStreamingChat({ store }: UseStreamingChatOptions): UseStreamingChat {
  const busyRef = useRef(false);

  const sendSegment = useCallback(
    async (audioB64: string, durationSec: number, model: ModelId) => {
      if (busyRef.current) {
        console.warn('[Phase4a/chat] sendSegment skipped (already busy)');
        return;
      }
      busyRef.current = true;
      store.setState((s) => ({ ...s, status: 'inferring' }));
      console.info('[Phase4a/chat] sendSegment', {
        model,
        durationSec: Number(durationSec.toFixed(2)),
        audioBytes: audioB64.length,
        historyTurns: store.getState().turns.length,
      });

      const segmentId = `seg-${Date.now().toString(36)}`;
      store.getState().recordUser({ segmentId, durationSec });

      const assistantId = store.getState().beginAssistant(model);
      const request: ChatRequest = {
        audio_b64: audioB64,
        history: store
          .getState()
          .turns.filter((t) => t.id !== assistantId)
          .map((t) => ({ role: 'model' as const, text: t.text })),
        model,
        system_prompt: CONVERSATION_PROMPT,
        client_metadata: {
          vad_speech_ms: Math.round(durationSec * 1000),
          recorded_at: new Date().toISOString(),
        },
      };

      const provider = model === 'gemini-2.5-flash' ? streamGeminiChat : streamOpenAIChat;
      const startedAt = performance.now();
      let firstTokenAt: number | null = null;

      try {
        for await (const e of provider(request) as AsyncGenerator<SSEEvent>) {
          if (e.type === 'token') {
            if (firstTokenAt === null) {
              firstTokenAt = performance.now();
              console.info(
                '[Phase4a/chat] first token after',
                Math.round(firstTokenAt - startedAt),
                'ms',
              );
            }
            store.getState().appendTokens(e.text);
          } else if (e.type === 'error') {
            console.error('[Phase4a/chat] upstream error event', e.code, e.message);
            store.getState().appendTokens(`\n[error: ${e.code} ${e.message}]`);
            break;
          } else if (e.type === 'done') {
            console.info('[Phase4a/chat] done');
            break;
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[Phase4a/chat] fetch error', err);
        store.getState().appendTokens(`\n[fetch error: ${msg}]`);
      } finally {
        const total = performance.now() - startedAt;
        store.getState().finalizeAssistant({
          first_token_ms: firstTokenAt ? Math.round(firstTokenAt - startedAt) : undefined,
          total_ms: Math.round(total),
        });
        store.setState((s) => ({ ...s, status: 'listening' }));
        busyRef.current = false;
      }
    },
    [store],
  );

  return { sendSegment };
}
